// hooks
import { MutationParams, type Pagination, useCommonMutation, useCommonQuery } from 'hooks';

// modules
import type {
  IUser,
  ReqGetUserDetail,
  ReqRegisterManager,
  ReqUserList,
  UserDetailInfo
} from '../models/user.model';
import type {
  BaseApiResponse,
  CommonQueryOptions,
  ReqMasterLogin,
  ResMasterLogin,
  ResultError
} from 'modules';

// apis
import { UserAPIV2 } from '../apis/user.api';

// keys
import { userKeys } from '../keys/user.key';

// atoms
import { UserInfoAtom } from '../atoms/user.atom';

// libraries
import { useSetRecoilState } from 'recoil';
import { UseMutationResult } from '@tanstack/react-query';

export const userApi = new UserAPIV2();

/** 관리자 페이지 조회 쿼리 훅 */
export const useGetUserList = ({
  data,
  options
}: {
  data: ReqUserList;
  options?: CommonQueryOptions<BaseApiResponse<unknown, IUser>, ResultError, IUser[]>;
}) => {
  const queryKeyFactory = (data: ReqUserList) => userKeys.GET_USER_LIST(data);

  const queryFn = async (data: ReqUserList, pagination: Pagination) => {
    const req = {
      ...data,
      page: pagination.current,
      size: pagination.pageSize
    };

    return await userApi.getUserList(req);
  };

  return useCommonQuery<ReqUserList, BaseApiResponse<unknown, IUser>, ResultError, IUser[]>({
    data,
    options: {
      ...options,
      select: (data) => data.resultList
    },
    queryKeyFactory,
    queryFn
  });
};

/** 관리자 상세 조회 쿼리 훅 */
export const useGetUsersId = ({
  data,
  options
}: {
  data: ReqGetUserDetail;
  options?: CommonQueryOptions<BaseApiResponse<UserDetailInfo>, ResultError, UserDetailInfo>;
}) => {
  const setUserInfo = useSetRecoilState(UserInfoAtom);

  const queryKeyFactory = (data: ReqGetUserDetail) => userKeys.GET_USER_DETAIL(data.userId);

  const queryFn = async (data: ReqGetUserDetail) => {
    return await userApi.getUsersId(data);
  };

  return useCommonQuery<
    ReqGetUserDetail,
    BaseApiResponse<UserDetailInfo>,
    ResultError,
    UserDetailInfo
  >({
    data,
    options: {
      ...options,
      select: (data) => data.resultObject,
      afterSuccess: (res) => {
        if (options?.afterSuccess) {
          options?.afterSuccess?.(res);
        } else {
          setUserInfo(res);
        }
      }
    },
    queryKeyFactory,
    queryFn
  });
};

/** 관리자가 관리자 등록 쿼리 훅 */
export const usePostRegisterManger = (): UseMutationResult<
  BaseApiResponse<UserDetailInfo>,
  ResultError,
  MutationParams<BaseApiResponse<UserDetailInfo>, ReqRegisterManager>,
  unknown
> => {
  return useCommonMutation<BaseApiResponse<UserDetailInfo>, ResultError, ReqRegisterManager>({
    mutationKey: userKeys.USER_ALL,
    mutationFn: async (data) => {
      const { userInfo, file } = data;

      const formData = new FormData();

      if (data.file) {
        formData.append('file', file);
      }

      formData.append('user', JSON.stringify(userInfo));

      return await userApi.postRegisterUser(formData);
    },
    queryKeyToInvalidate: userKeys.USER_ALL
  });
};

/** 관리자가 관리자 수정 쿼리 훅 */
export const usePutUpdateManger = (): UseMutationResult<
  BaseApiResponse<UserDetailInfo>,
  ResultError,
  MutationParams<BaseApiResponse<UserDetailInfo>, ReqRegisterManager>,
  unknown
> => {
  return useCommonMutation<BaseApiResponse<UserDetailInfo>, ResultError, ReqRegisterManager>({
    mutationKey: userKeys.USER_ALL,
    mutationFn: async (data) => {
      const { userInfo, file } = data;

      const formData = new FormData();

      if (data.file) {
        formData.append('file', file);
      }

      formData.append('user', JSON.stringify(userInfo));

      return await userApi.putUpdateUser(formData);
    },
    queryKeyToInvalidate: userKeys.USER_ALL
  });
};

/** 관리자가 관리자 삭제 쿼리 훅 */
export const useDeleteManger = (): UseMutationResult<
  BaseApiResponse,
  ResultError,
  MutationParams<BaseApiResponse, string>,
  unknown
> => {
  return useCommonMutation<BaseApiResponse, ResultError, string>({
    mutationKey: userKeys.USER_ALL,
    mutationFn: async (userId) => await userApi.deleteUser(userId),
    queryKeyToInvalidate: userKeys.USER_ALL
  });
};

export const useMasterLogin = () => {
  return useCommonMutation<ResMasterLogin, ResultError, ReqMasterLogin>({
    mutationKey: userKeys.USER_ALL,
    mutationFn: async (data) => await userApi.masterLogin(data)
  });
};
